import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { T } from "@/lib/testIds";
import { ArrowRight, Award, GraduationCap, Home, BookMarked, Search, X, AlertCircle, Bell, ClipboardCheck } from "lucide-react";
import { categoryFor, tagsFor, CATEGORIES, LEVELS } from "@/lib/courseCategories";

function CourseCard({ c, p, i }) {
  // Backend now returns percent already clamped and reconciled with the current
  // course. We only fall back to a client-side calc if the backend didn't send
  // `percent` (older responses), and even then we clamp at 100 to prevent the
  // maths-impossible->100% bug when a course has been reseeded.
  let percent = 0;
  if (p) {
    if (typeof p.percent === "number") {
      percent = p.percent;
    } else {
      const denom = p.total_lessons || c?.lessons?.length || 0;
      percent = denom ? Math.round((p.completed_lessons.length / denom) * 100) : 0;
    }
    percent = Math.max(0, Math.min(100, percent));
  }
  // Refresh cadence badge — three states: overdue, due-soon (<30 days), or fine.
  let refreshBadge = null;
  if (p?.quiz_passed && p?.refresh_months && typeof p.days_until_due === "number") {
    if (p.is_overdue) {
      refreshBadge = { label: `Refresh overdue by ${Math.abs(p.days_until_due)}d`, tone: "danger" };
    } else if (p.days_until_due <= 30) {
      refreshBadge = { label: `Refresh in ${p.days_until_due}d`, tone: "warn" };
    }
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: i * 0.06 }}
      data-testid={T.dashboardCourseCard}
      className="bg-white border border-brand-linen rounded-3xl overflow-hidden shadow-card hover:shadow-cardHover hover:-translate-y-1 transition-all"
    >
      {c.thumbnail && (
        <div className="aspect-[16/10] overflow-hidden">
          <img src={c.thumbnail} alt={`${c.title} — course cover`} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-muted">
            {c.level}{c.duration ? ` · ${c.duration}` : ""}
          </div>
          {refreshBadge && (
            <span
              data-testid={`refresh-badge-${c.slug}`}
              className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full inline-flex items-center gap-1 whitespace-nowrap flex-shrink-0 ${
                refreshBadge.tone === "danger"
                  ? "bg-brand-accent text-white"
                  : "bg-brand-linen text-brand-ink"
              }`}
            >
              <AlertCircle className="w-3 h-3" strokeWidth={2} />
              {refreshBadge.label}
            </span>
          )}
        </div>
        <h3 className="font-serif text-2xl text-brand-ink mt-2 leading-tight">{c.title}</h3>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`course-tags-${c.slug}`}>
          {tagsFor(c).map((tag) => (
            <span
              key={tag}
              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-brand-linen text-brand-ink/80"
            >
              {tag}
            </span>
          ))}
        </div>
        <p className="text-brand-body text-sm mt-3 leading-relaxed line-clamp-3">{c.subtitle || c.description}</p>

        <div className="mt-5">
          <div className="flex justify-between text-xs text-brand-muted mb-2">
            <span>Progress</span><span>{percent}%</span>
          </div>
          <div className="h-1.5 bg-brand-linen rounded-full overflow-hidden">
            <div className="h-full bg-brand-ink transition-all" style={{ width: `${percent}%` }} />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <Link
            to={`/courses/${c.slug}`}
            data-testid={T.dashboardStartCourse}
            className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-bone rounded-full px-4 py-2 text-sm hover:bg-brand-inkHover"
          >
            {percent > 0 ? "Continue" : "Start"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
          {p?.quiz_passed && (
            <Link
              to={`/courses/${c.slug}/certificate`}
              data-testid={T.dashboardCertificateLink}
              className="inline-flex items-center gap-1.5 border border-brand-linen text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen"
            >
              <Award className="w-3.5 h-3.5" strokeWidth={1.5} /> Course certificate
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progressBySlug, setProgressBySlug] = useState({});
  const [starterPath, setStarterPath] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    (async () => {
      const results = await Promise.allSettled([
        api.get("/courses"),
        api.get("/progress"),
        api.get("/starter-path"),
        api.get("/assignments"),
      ]);
      if (results[0].status === "fulfilled") setCourses(results[0].value.data);
      if (results[1].status === "fulfilled") {
        const map = {};
        results[1].value.data.forEach((p) => (map[p.course_slug] = p));
        setProgressBySlug(map);
      }
      if (results[2].status === "fulfilled") setStarterPath(results[2].value.data);
      if (results[3].status === "fulfilled") setAssignments(results[3].value.data.assignments || []);
    })();
  }, []);

  const { practiceCourses, communityCourses } = useMemo(() => {
    const practiceCourses = [];
    const communityCourses = [];
    for (const c of courses) {
      if (c.practice_id) practiceCourses.push(c);
      else communityCourses.push(c);
    }
    return { practiceCourses, communityCourses };
  }, [courses]);

  // Categories present across the whole library — union of all tags on all courses.
  const availableCategories = useMemo(() => {
    const set = new Set();
    for (const c of communityCourses) tagsFor(c).forEach((t) => set.add(t));
    return CATEGORIES.filter((c) => set.has(c)).concat(
      [...set].filter((c) => !CATEGORIES.includes(c)).sort()
    );
  }, [communityCourses]);

  const availableLevels = useMemo(() => {
    const set = new Set(communityCourses.map((c) => c.level).filter(Boolean));
    return LEVELS.filter((l) => set.has(l)).concat(
      [...set].filter((l) => !LEVELS.includes(l)).sort()
    );
  }, [communityCourses]);

  const filteredCommunity = useMemo(() => {
    const q = query.trim().toLowerCase();
    return communityCourses.filter((c) => {
      if (selectedCategories.length) {
        const courseTags = tagsFor(c);
        // OR match — course must carry at least one of the selected tags
        if (!selectedCategories.some((t) => courseTags.includes(t))) return false;
      }
      if (selectedLevels.length && !selectedLevels.includes(c.level)) return false;
      if (!q) return true;
      const hay = `${c.title || ""} ${c.subtitle || ""} ${c.description || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [communityCourses, query, selectedCategories, selectedLevels]);

  // Refresh reminders — courses the learner has already passed whose next
  // refresh is overdue or due within the next 30 days. Sorted so overdue
  // shows first, then soonest-due next.
  const refreshDue = useMemo(() => {
    const rows = [];
    const bySlug = {};
    for (const c of courses) bySlug[c.slug] = c;
    for (const slug of Object.keys(progressBySlug)) {
      const p = progressBySlug[slug];
      const c = bySlug[slug];
      if (!c || !p?.quiz_passed || !p?.refresh_months) continue;
      if (typeof p.days_until_due !== "number") continue;
      if (p.is_overdue) {
        rows.push({ slug, title: c.title, days: p.days_until_due, overdue: true });
      } else if (p.days_until_due <= 30) {
        rows.push({ slug, title: c.title, days: p.days_until_due, overdue: false });
      }
    }
    rows.sort((a, b) => {
      if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
      return a.days - b.days;
    });
    return rows;
  }, [courses, progressBySlug]);

  const filtersActive = query || selectedCategories.length || selectedLevels.length;
  const clearFilters = () => {
    setQuery("");
    setSelectedCategories([]);
    setSelectedLevels([]);
  };
  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-brand-muted mb-2">Your ledger</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink">Good morning, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-brand-body mt-2">Pick a course below to continue.</p>
        </div>
      </div>

      {refreshDue.length > 0 && (
        <section
          data-testid="dashboard-refresh-reminders"
          className={`mb-10 rounded-3xl border p-6 sm:p-7 ${
            refreshDue.some((r) => r.overdue)
              ? "bg-brand-accent/5 border-brand-accent/30"
              : "bg-amber-50/60 border-amber-200"
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3 min-w-0">
              <span
                className={`mt-0.5 flex-shrink-0 rounded-full p-2 ${
                  refreshDue.some((r) => r.overdue)
                    ? "bg-brand-accent/15 text-brand-accent"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                <Bell className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.28em] text-brand-muted">
                  Time to refresh
                </div>
                <h2 className="font-serif text-2xl text-brand-ink mt-1">
                  {refreshDue.length === 1
                    ? "One certified course needs your attention"
                    : `${refreshDue.length} certified courses need your attention`}
                </h2>
                <p className="text-sm text-brand-body mt-1">
                  Retake the quiz to stay compliant — it takes a few minutes and refreshes your
                  certificate.
                </p>
              </div>
            </div>
          </div>
          <ul className="grid gap-2 sm:grid-cols-2">
            {refreshDue.map((r) => (
              <li key={r.slug}>
                <Link
                  to={`/courses/${r.slug}`}
                  data-testid={`refresh-reminder-${r.slug}`}
                  className="flex items-center justify-between gap-3 bg-white rounded-2xl border border-brand-linen px-4 py-3 hover:border-brand-ink/40 hover:-translate-y-0.5 transition-all"
                >
                  <div className="min-w-0">
                    <div className="font-serif text-brand-ink truncate">{r.title}</div>
                    <div
                      className={`text-xs mt-0.5 ${
                        r.overdue ? "text-brand-accent font-medium" : "text-brand-muted"
                      }`}
                    >
                      {r.overdue
                        ? `Overdue by ${Math.abs(r.days)}d`
                        : `Due in ${r.days}d`}
                    </div>
                  </div>
                  <ArrowRight
                    className="w-4 h-4 text-brand-muted flex-shrink-0"
                    strokeWidth={1.5}
                  />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {assignments.filter((a) => a.status !== "completed").length > 0 && (
        <section
          data-testid="dashboard-assignments"
          className="mb-10 rounded-3xl border border-brand-secondary/30 bg-brand-secondary/5 p-6 sm:p-7"
        >
          <div className="flex items-start gap-3 mb-4">
            <span className="mt-0.5 flex-shrink-0 rounded-full p-2 bg-brand-secondary/15 text-brand-secondary">
              <ClipboardCheck className="w-4 h-4" strokeWidth={1.5} />
            </span>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-brand-muted">Assigned to you</div>
              <h2 className="font-serif text-2xl text-brand-ink mt-1">
                {(() => {
                  const n = assignments.filter((a) => a.status !== "completed").length;
                  return n === 1 ? "Your Practice Manager has assigned one course" : `Your Practice Manager has assigned ${n} courses`;
                })()}
              </h2>
              <p className="text-sm text-brand-body mt-1">Complete these to keep your training record up to date.</p>
            </div>
          </div>
          <ul className="grid gap-2 sm:grid-cols-2">
            {assignments
              .filter((a) => a.status !== "completed")
              .map((a) => {
                const due = a.due_at ? new Date(a.due_at) : null;
                const overdue = due && due.getTime() < Date.now();
                return (
                  <li key={a.id}>
                    <Link
                      to={`/courses/${a.course_slug}`}
                      data-testid={`assigned-course-${a.course_slug}`}
                      className="flex items-center justify-between gap-3 bg-white rounded-2xl border border-brand-linen px-4 py-3 hover:border-brand-ink/40 hover:-translate-y-0.5 transition-all"
                    >
                      <div className="min-w-0">
                        <div className="font-serif text-brand-ink truncate">{a.course_title}</div>
                        <div className={`text-xs mt-0.5 ${overdue ? "text-brand-accent font-medium" : "text-brand-muted"}`}>
                          Assigned by {a.assigned_by_name || "your PM"}
                          {due && (overdue
                            ? ` · Overdue since ${due.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                            : ` · Due ${due.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`)}
                        </div>
                        {a.notes && (
                          <div className="text-xs italic text-brand-muted mt-1 truncate" title={a.notes}>
                            “{a.notes}”
                          </div>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-brand-muted flex-shrink-0" strokeWidth={1.5} />
                    </Link>
                  </li>
                );
              })}
          </ul>
        </section>
      )}

      {starterPath && !starterPath.role_in_practice && (
        <section data-testid="dashboard-role-prompt" className="mb-14 bg-brand-linen/40 border border-brand-linen rounded-3xl p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.28em] text-brand-muted">One question</div>
              <h2 className="font-serif text-2xl text-brand-ink mt-1">What&rsquo;s your role at the practice?</h2>
              <p className="text-sm text-brand-body mt-2">Choose your role so Calside can recommend a starter path of the courses most relevant to your day-to-day work.</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                data-testid="role-picker"
                onChange={async (e) => {
                  const v = e.target.value;
                  if (!v) return;
                  try {
                    await api.post("/auth/role", { role_in_practice: v });
                    const sp = await api.get("/starter-path");
                    setStarterPath(sp.data);
                  } catch (err) { console.error(err); }
                }}
                className="px-4 py-3 border border-brand-linen rounded-xl bg-white focus:outline-none focus:border-brand-ink transition-colors"
              >
                <option value="">— Choose —</option>
                <option value="receptionist">Receptionist</option>
                <option value="secretary">Secretary</option>
                <option value="workflow_admin">Workflow admin</option>
                <option value="prescription_clerk">Prescription clerk</option>
                <option value="office_manager">Assistant / Office Manager</option>
                <option value="clinician">Clinical (nurse, HCA, ANP, pharmacist)</option>
                <option value="pm">Practice Manager</option>
                <option value="new_starter">New starter</option>
                <option value="other">Other / Attached</option>
              </select>
            </div>
          </div>
        </section>
      )}

      {starterPath?.role_in_practice && starterPath.courses?.length > 0 && (
        <section data-testid="dashboard-section-starter-path" className="mb-14 bg-brand-ink text-brand-bone rounded-3xl p-8 sm:p-10">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-brand-bone/60">Your starter path</div>
              <h2 className="font-serif text-3xl text-brand-bone mt-1">Recommended for {starterPath.role_label}</h2>
            </div>
            <div className="text-xs text-brand-bone/70">
              {starterPath.courses.filter((c) => c.passed).length} of {starterPath.courses.length} passed
            </div>
          </div>
          <p className="text-sm text-brand-bone/75 max-w-2xl mb-6">
            The core courses we think every {starterPath.role_label.toLowerCase()} should complete first. Others in the Calside library are optional — pick and mix as you go.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {starterPath.courses.map((sc) => (
              <Link
                key={sc.slug}
                to={`/courses/${sc.slug}`}
                data-testid={`starter-course-${sc.slug}`}
                className={`flex items-center gap-3 rounded-2xl border px-4 py-3 transition-colors ${
                  sc.passed
                    ? "border-brand-bone/20 bg-brand-bone/5 text-brand-bone/70"
                    : "border-brand-bone/30 hover:border-brand-bone hover:bg-brand-bone/5 text-brand-bone"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-snug line-clamp-2 sm:truncate">{sc.title}</div>
                  {sc.duration && <div className="text-[11px] text-brand-bone/50 truncate mt-0.5">{sc.duration}</div>}
                </div>
                {sc.passed ? (
                  <Award className="w-4 h-4 text-brand-bone/50 flex-shrink-0" strokeWidth={1.7} />
                ) : (
                  <ArrowRight className="w-4 h-4 flex-shrink-0" strokeWidth={1.7} />
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {practiceCourses.length > 0 && (
        <section data-testid="dashboard-section-practice" className="mb-14">
          <div className="flex items-baseline gap-3 mb-6">
            <Home className="w-4 h-4 text-brand-accent" strokeWidth={1.7} />
            <h2 className="font-serif text-2xl text-brand-ink">Your practice&apos;s courses</h2>
            <span className="text-xs uppercase tracking-widest text-brand-muted">
              {practiceCourses.length} course{practiceCourses.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {practiceCourses.map((c, i) => (
              <CourseCard key={c.id} c={c} p={progressBySlug[c.slug]} i={i} />
            ))}
          </div>
        </section>
      )}

      <section data-testid="dashboard-section-community">
        <div className="flex items-baseline gap-3 mb-6">
          <BookMarked className="w-4 h-4 text-brand-accent" strokeWidth={1.7} />
          <h2 className="font-serif text-2xl text-brand-ink">Calside library</h2>
          <span className="text-xs uppercase tracking-widest text-brand-muted">
            Community · {communityCourses.length} courses
          </span>
        </div>

        {/* Filter bar */}
        <div
          data-testid="library-filter-bar"
          className="mb-8 rounded-2xl border border-brand-linen bg-white/70 backdrop-blur px-5 py-4"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.7} />
              <input
                data-testid="library-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search courses by title, topic or keyword…"
                className="w-full pl-9 pr-9 py-2.5 rounded-full bg-brand-linen/60 border border-transparent focus:border-brand-ink/20 focus:outline-none text-sm text-brand-ink placeholder:text-brand-muted transition-colors"
              />
              {query && (
                <button
                  data-testid="library-search-clear"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-brand-linen"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5 text-brand-muted" strokeWidth={1.7} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-brand-muted whitespace-nowrap">
              <span data-testid="library-result-count">
                Showing <span className="text-brand-ink font-medium">{filteredCommunity.length}</span> of {communityCourses.length}
              </span>
              {filtersActive && (
                <button
                  data-testid="library-clear-filters"
                  onClick={clearFilters}
                  className="ml-2 inline-flex items-center gap-1 text-brand-accent hover:underline"
                >
                  <X className="w-3 h-3" strokeWidth={2} /> Clear filters
                </button>
              )}
            </div>
          </div>

          {availableCategories.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-2">Category</div>
              <div className="flex flex-wrap gap-2">
                {availableCategories.map((cat) => {
                  const active = selectedCategories.includes(cat);
                  return (
                    <button
                      key={cat}
                      data-testid={`library-cat-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => toggle(selectedCategories, setSelectedCategories, cat)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        active
                          ? "bg-brand-ink text-brand-bone border-brand-ink"
                          : "bg-white text-brand-ink border-brand-linen hover:border-brand-ink/40"
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {availableLevels.length > 1 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mb-2">Level</div>
              <div className="flex flex-wrap gap-2">
                {availableLevels.map((lvl) => {
                  const active = selectedLevels.includes(lvl);
                  return (
                    <button
                      key={lvl}
                      data-testid={`library-level-${lvl.toLowerCase()}`}
                      onClick={() => toggle(selectedLevels, setSelectedLevels, lvl)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        active
                          ? "bg-brand-accent text-white border-brand-accent"
                          : "bg-white text-brand-ink border-brand-linen hover:border-brand-accent/60"
                      }`}
                    >
                      {lvl}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {filteredCommunity.length === 0 ? (
          <div
            data-testid="library-empty-state"
            className="rounded-2xl border border-dashed border-brand-linen bg-white/40 py-16 text-center"
          >
            <p className="font-serif text-xl text-brand-ink">No courses match those filters.</p>
            <p className="text-brand-body text-sm mt-2">Try a different keyword or clear a filter.</p>
            <button
              onClick={clearFilters}
              className="mt-4 inline-flex items-center gap-1 text-sm text-brand-accent hover:underline"
              data-testid="library-empty-clear"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2} /> Clear filters
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="library-grid">
            {filteredCommunity.map((c, i) => (
              <CourseCard key={c.id} c={c} p={progressBySlug[c.slug]} i={i} />
            ))}
          </div>
        )}
      </section>

      {user?.role === "admin" && (
        <div className="mt-12 rounded-3xl bg-white border border-brand-linen p-8 shadow-card">
          <div className="flex items-center gap-3">
            <GraduationCap className="w-5 h-5 text-brand-accent" strokeWidth={1.5} />
            <div className="text-xs uppercase tracking-widest text-brand-muted">Admin</div>
          </div>
          <h3 className="font-serif text-3xl text-brand-ink mt-3">You have admin privileges.</h3>
          <p className="text-brand-body mt-2">Head to the admin dashboard to author your own practice courses and view team progress.</p>
          <Link to="/admin" className="inline-flex items-center gap-2 mt-4 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover">
            Open admin dashboard <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      )}
    </Layout>
  );
}
